//
//  SDKTokenTheme.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import { TokenType } from "../enums/SDKTokenType";
import { BlurToken } from "../tokens/SDKBlurToken";
import { BorderToken } from "../tokens/SDKBorderToken";
import { ColorToken } from "../tokens/SDKColorToken";
import { BorderWidthToken, DimensionToken, DurationToken, FontSizeToken, LetterSpacingToken, LineHeightToken, OpacityToken, ParagraphSpacingToken, RadiusToken, SizeToken, SpaceToken, ZIndexToken, } from "../tokens/SDKDimensionToken";
import { GradientToken } from "../tokens/SDKGradientToken";
import { ShadowToken } from "../tokens/SDKShadowToken";
import { FontFamilyToken, FontWeightToken, ProductCopyToken, StringToken, } from "../tokens/SDKStringToken";
import { TextCaseToken } from "../tokens/SDKTextCaseToken";
import { TextDecorationToken } from "../tokens/SDKTextDecorationToken";
import { TypographyToken } from "../tokens/SDKTypographyToken";
import { VisibilityToken } from "../tokens/SDKVisibilityToken";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class TokenTheme {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(model, versionId, collectionName) {
        var _a, _b;
        this.id = model.persistentId;
        // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        this.idInVersion = model.id;
        this.brandId = model.brandId;
        this.designSystemVersionId = versionId;
        this.name = model.meta.name;
        this.description = model.meta.description;
        this.codeName = model.codeName;
        this.createdAt = model.createdAt ? new Date(model.createdAt) : null;
        this.updatedAt = model.updatedAt ? new Date(model.updatedAt) : null;
        // Note overrides are provided from the resolver when they are computed
        this.overriddenTokens = [];
        this.parentPersistentId = (_a = model.parentPersistentId) !== null && _a !== void 0 ? _a : null;
        this.collectionPersistentIds = (_b = model.collectionPersistentIds) !== null && _b !== void 0 ? _b : [];
        this.collectionName = collectionName;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Convenience
    addOverride(override) {
        this.overriddenTokens.push(override);
    }
    addOverrides(overrides) {
        this.overriddenTokens = this.overriddenTokens.concat(overrides);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Manipulation
    toWriteObject() {
        var _a, _b;
        return {
            id: this.idInVersion,
            brandId: this.brandId,
            designSystemVersionId: this.designSystemVersionId,
            persistentId: this.id,
            meta: {
                name: this.name,
                description: (_a = this.description) !== null && _a !== void 0 ? _a : "",
            },
            createdAt: this.createdAt ? this.createdAt.toISOString() : undefined,
            updatedAt: this.updatedAt ? this.updatedAt.toISOString() : undefined,
            codeName: this.codeName,
            overrides: this.tokensToOverrides(),
            parentPersistentId: (_b = this.parentPersistentId) !== null && _b !== void 0 ? _b : undefined,
            collectionPersistentIds: this.collectionPersistentIds,
        };
    }
    tokensToOverrides() {
        return this.overriddenTokens.map((o) => this.toWriteOverrideObject(o));
    }
    toWriteOverrideObject(token) {
        var _a, _b, _c;
        const model = {
            data: this.valueToWriteObject(token.value, token.tokenType, token),
            tokenPersistentId: token.id,
            type: token.tokenType,
            origin: token.origin && token.origin.id && token.origin.sourceId
                ? {
                    id: (_a = token.origin.id) !== null && _a !== void 0 ? _a : undefined,
                    name: (_b = token.origin.name) !== null && _b !== void 0 ? _b : undefined,
                    sourceId: (_c = token.origin.sourceId) !== null && _c !== void 0 ? _c : undefined,
                }
                : undefined,
            createdAt: token.createdAt ? token.createdAt.toISOString() : undefined,
            updatedAt: token.updatedAt ? token.updatedAt.toISOString() : undefined,
        };
        if (!model.data.aliasTo && !model.data.value) {
            throw new Error("Token doesn't have value or alias to");
        }
        // @ts-expect-error TS(2322): Type '{ data: { aliasTo: string | undefined; value... Remove this comment to see the full error message
        return model;
    }
    valueToWriteObject(value, type, token) {
        switch (type) {
            case TokenType.blur:
                return BlurToken.valueToWriteObject(value);
            case TokenType.border:
                return BorderToken.valueToWriteObject(value);
            case TokenType.color:
                return ColorToken.valueToWriteObject(value);
            case TokenType.gradient:
                return GradientToken.valueToWriteObject(value);
            case TokenType.dimension:
                return DimensionToken.valueToWriteObject(value);
            case TokenType.size:
                return SizeToken.valueToWriteObject(value);
            case TokenType.space:
                return SpaceToken.valueToWriteObject(value);
            case TokenType.opacity:
                return OpacityToken.valueToWriteObject(value);
            case TokenType.fontSize:
                return FontSizeToken.valueToWriteObject(value);
            case TokenType.lineHeight:
                return LineHeightToken.valueToWriteObject(value);
            case TokenType.letterSpacing:
                return LetterSpacingToken.valueToWriteObject(value);
            case TokenType.paragraphSpacing:
                return ParagraphSpacingToken.valueToWriteObject(value);
            case TokenType.borderWidth:
                return BorderWidthToken.valueToWriteObject(value);
            case TokenType.radius:
                return RadiusToken.valueToWriteObject(value);
            case TokenType.duration:
                return DurationToken.valueToWriteObject(value);
            case TokenType.zIndex:
                return ZIndexToken.valueToWriteObject(value);
            case TokenType.shadow:
                return ShadowToken.valueToWriteObject(value);
            case TokenType.string:
                return StringToken.valueToWriteObject(value);
            case TokenType.productCopy:
                return ProductCopyToken.valueToWriteObject(value);
            case TokenType.fontFamily:
                return FontFamilyToken.valueToWriteObject(value);
            case TokenType.fontWeight:
                return FontWeightToken.valueToWriteObject(value);
            case TokenType.typography:
                return TypographyToken.valueToWriteObject(value);
            case TokenType.textCase:
                return TextCaseToken.valueToWriteObject(value);
            case TokenType.textDecoration:
                return TextDecorationToken.valueToWriteObject(value);
            case TokenType.visibility:
                return VisibilityToken.valueToWriteObject(value);
            default:
                throw new UnreachableCaseError(type);
        }
    }
}
